import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Loader2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_LABELS = {
  top: "Back To Top",
  services: "See What We Haul",
  hauling: "See Hauling Rates",
  proof: "See The Proof",
  reviews: "Read Reviews",
  pricing: "See Pricing",
  faq: "Read The FAQ",
  contact: "Go To The Quote Form",
};

const QUICK_CHIPS = [
  { label: "Pricing", message: "How does your pricing work?", slug: "pricing" },
  { label: "What Do You Haul?", message: "What kind of items do you haul away?", slug: "items" },
  { label: "Service Areas", message: "What areas do you serve?", slug: "areas" },
  { label: "Book Same-Day", message: "How do I book a same-day pickup?", slug: "book" },
];

const WELCOME = {
  id: "welcome",
  role: "bot",
  text: "I'm Haul, the Godspeed guide. Ask me about pricing, what we take, or how to book — I'll point you to the right spot.",
};

const ERROR_TEXT = "Connection hiccup. Try again — or call (470) 684-0150.";

let msgCounter = 0;
const nextId = () => `m-${Date.now()}-${msgCounter++}`;

const getSessionId = () => {
  let id = localStorage.getItem("gs_chat_session");
  if (!id) {
    id = `gs-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("gs_chat_session", id);
  }
  return id;
};

const patchLast = (list, patch) => {
  const copy = [...list];
  const last = copy[copy.length - 1];
  copy[copy.length - 1] = { ...last, ...(typeof patch === "function" ? patch(last) : patch) };
  return copy;
};

const parseSseEvent = (part) => {
  const line = part.trim();
  if (!line.startsWith("data:")) return null;
  const payload = line.slice(5).trim();
  if (payload === "[DONE]") return null;
  try {
    return JSON.parse(payload).t || null;
  } catch (err) {
    console.warn("[chat] skipped malformed SSE chunk", err);
    return null;
  }
};

const readSseStream = async (res, onToken) => {
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop();
    parts.map(parseSseEvent).filter(Boolean).forEach(onToken);
  }
};

const useChat = () => {
  const [messages, setMessages] = useState([WELCOME]);
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(getSessionId());

  const send = async (message) => {
    if (!message || busy) return;
    setBusy(true);
    setMessages((m) => [...m, { id: nextId(), role: "user", text: message }, { id: nextId(), role: "bot", text: "", streaming: true }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message }),
      });
      if (!res.ok) throw new Error(`chat request failed: ${res.status}`);
      await readSseStream(res, (t) => setMessages((m) => patchLast(m, (last) => ({ text: last.text + t }))));
    } catch (err) {
      console.error("[chat] send failed", err);
      setMessages((m) => patchLast(m, { text: ERROR_TEXT }));
    } finally {
      setMessages((m) => patchLast(m, { streaming: false }));
      setBusy(false);
    }
  };

  return { messages, busy, send };
};

const NavButton = ({ id, onNav }) => (
  <button
    type="button"
    onClick={() => onNav(id)}
    data-testid={`chat-nav-button-${id}`}
    className="mt-3 flex w-fit items-center gap-2 border-2 border-gold bg-gold/10 px-4 py-2 font-body text-xs font-bold uppercase tracking-[0.15em] text-gold transition-colors duration-200 hover:bg-gold hover:text-ink"
  >
    {NAV_LABELS[id] || "Take Me There"} <ArrowRight size={14} />
  </button>
);

const BotMessage = ({ text, onNav, streaming }) => {
  const navIds = [...text.matchAll(/\[\[NAV:(\w+)\]\]/g)].map((m) => m[1]);
  const clean = text.replace(/\[\[NAV:\w+\]\]/g, "").trim();
  return (
    <div className="border-2 border-coal bg-panel px-4 py-3 text-sm leading-relaxed text-white/90">
      {clean}
      {streaming && <span className="ml-1 inline-block h-4 w-2 animate-pulse bg-gold align-middle" aria-hidden="true" />}
      {!streaming && navIds.map((id) => <NavButton key={id} id={id} onNav={onNav} />)}
    </div>
  );
};

const MessageList = ({ messages, onNav, scrollRef }) => (
  <div ref={scrollRef} data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto p-4">
    {messages.map((msg, i) =>
      msg.role === "user" ? (
        <div key={msg.id} className="ml-auto w-fit max-w-[85%] border-2 border-gold bg-gold px-4 py-2.5 text-sm font-semibold text-ink" data-testid={`chat-message-user-${i}`}>
          {msg.text}
        </div>
      ) : (
        <div key={msg.id} className="mr-auto max-w-[92%]" data-testid={`chat-message-bot-${i}`}>
          <BotMessage text={msg.text} onNav={onNav} streaming={msg.streaming} />
        </div>
      )
    )}
  </div>
);

const QuickChips = ({ busy, onPick }) => (
  <div className="shrink-0 border-t-2 border-coal px-4 pb-2 pt-3">
    <div className="flex flex-wrap gap-1.5">
      {QUICK_CHIPS.map((c) => (
        <button
          key={c.slug}
          type="button"
          onClick={() => onPick(c.message)}
          disabled={busy}
          data-testid={`chat-quick-chip-${c.slug}`}
          className="border border-coal px-2.5 py-1 font-body text-[11px] font-bold uppercase tracking-[0.1em] text-white/70 transition-colors duration-200 hover:border-gold hover:text-gold disabled:opacity-50"
        >
          {c.label}
        </button>
      ))}
    </div>
  </div>
);

const ChatInput = ({ busy, onSend }) => {
  const [input, setInput] = useState("");
  const submit = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    setInput("");
    onSend(text);
  };
  return (
    <form className="flex shrink-0 gap-2 border-t-2 border-coal p-3" onSubmit={submit}>
      <input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        data-testid="chat-input"
        placeholder="Ask about pricing, booking, areas..."
        className="input-brutal flex-1 !py-2.5 text-sm"
      />
      <button
        type="submit"
        disabled={busy || !input.trim()}
        data-testid="chat-send-button"
        aria-label="Send message"
        className="flex w-12 shrink-0 items-center justify-center border-2 border-gold bg-gold text-ink transition-colors duration-200 hover:bg-gold-light disabled:opacity-50"
      >
        {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
      </button>
    </form>
  );
};

const ChatPanel = ({ onClose, onNav, chat, scrollRef }) => (
  <div
    data-testid="chat-panel"
    className="fixed bottom-[7.75rem] right-4 z-50 flex h-[26rem] w-[calc(100vw-2rem)] max-w-sm flex-col border-2 border-gold bg-ink shadow-hard-black lg:bottom-[6.5rem] lg:right-6"
  >
    <div className="hazard-thin h-2 w-full shrink-0" aria-hidden="true" />
    <div className="flex shrink-0 items-center justify-between border-b-2 border-coal px-4 py-3">
      <div>
        <p className="font-heading text-xl uppercase tracking-wide text-gold">Godspeed Guide</p>
        <p className="font-body text-[11px] font-bold uppercase tracking-[0.15em] text-white/50">Ask Me Anything</p>
      </div>
      <button type="button" onClick={onClose} data-testid="chat-close-button" aria-label="Close chat" className="p-1 text-white/60 transition-colors hover:text-gold">
        <X size={20} />
      </button>
    </div>
    <MessageList messages={chat.messages} onNav={onNav} scrollRef={scrollRef} />
    <QuickChips busy={chat.busy} onPick={chat.send} />
    <ChatInput busy={chat.busy} onSend={chat.send} />
  </div>
);

export const ChatBot = () => {
  const [open, setOpen] = useState(false);
  const chat = useChat();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [chat.messages, open]);

  const onNav = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="chat-toggle-button"
        aria-label={open ? "Close chat guide" : "Open chat guide"}
        className="btn-brutal shadow-hard-sm fixed bottom-[4.5rem] right-4 z-50 flex h-14 w-14 items-center justify-center border-2 border-gold bg-gold text-ink lg:bottom-6 lg:right-6"
      >
        {open ? <X size={24} /> : <MessageSquare size={24} />}
      </button>
      {open && <ChatPanel onClose={() => setOpen(false)} onNav={onNav} chat={chat} scrollRef={scrollRef} />}
    </>
  );
};
